
import { useState } from "react";
import EpisodesHeader from "@/components/episodes/EpisodesHeader";
import EpisodesSearchFilter from "@/components/episodes/EpisodesSearchFilter";
import EpisodesGrid from "@/components/episodes/EpisodesGrid";
import EpisodesCTA from "@/components/episodes/EpisodesCTA";
import VideoEmbed from "@/components/VideoEmbed";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useDocumentHead } from "@/hooks/useDocumentHead";
import { usePagination } from "@/hooks/usePagination";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Episode, Category } from "@/components/episodes/types";

const Episodes = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<{
    youtubeId?: string;
    vimeoId?: string;
    videoPlatform?: "youtube" | "vimeo";
  } | null>(null);

  useDocumentHead({
    title: "Episodes - Bobo & Teedee®",
    description: "Watch all episodes of Bobo & Teedee®, the educational children's series featuring Cayman Islands culture and values.",
    keywords: "Bobo Teedee episodes, Cayman Islands children's show, educational videos, kids entertainment"
  });

  // Fetch episodes
  const { data: episodes = [], isLoading: episodesLoading } = useQuery({
    queryKey: ["episodes"],
    queryFn: async (): Promise<Episode[]> => {
      const { data, error } = await supabase
        .from("episodes")
        .select(`
          *,
          episodes_categories!episodes_category_id_fkey (
            id,
            name,
            color
          )
        `)
        .eq("status", "published")
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Add video_platform field with proper type casting
      return (data || []).map(episode => {
        const videoPlatform = episode.video_platform === "youtube" || episode.video_platform === "vimeo" 
          ? episode.video_platform 
          : (episode.vimeo_id ? "vimeo" as const : episode.youtube_id ? "youtube" as const : undefined);
        
        return {
          ...episode,
          video_platform: videoPlatform
        };
      });
    },
  });

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ["episodes-categories"],
    queryFn: async (): Promise<Category[]> => {
      const { data, error } = await supabase
        .from("episodes_categories")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  // Filter episodes based on search and category
  const filteredEpisodes = episodes.filter((episode) => {
    const matchesSearch = episode.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (episode.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesCategory = !selectedCategory || episode.episodes_categories?.id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination - Fix: use object format for usePagination hook
  const itemsPerPage = 9;
  const {
    currentPage,
    totalPages,
    paginatedItems: paginatedEpisodes,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
  } = usePagination({ items: filteredEpisodes, itemsPerPage });

  const isLoading = episodesLoading || categoriesLoading;

  const handleVideoPlay = (youtubeId?: string, vimeoId?: string, videoPlatform?: "youtube" | "vimeo") => {
    setSelectedVideo({ youtubeId, vimeoId, videoPlatform });
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-sky-blue/10 via-white to-bobo-green/10">
        <EpisodesHeader />
        
        <EpisodesSearchFilter
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          categories={categories}
        />

        <EpisodesGrid
          isLoading={isLoading}
          filteredEpisodes={filteredEpisodes}
          paginatedEpisodes={paginatedEpisodes}
          searchTerm={searchTerm}
          selectedCategory={selectedCategory}
          categories={categories}
          setSearchTerm={setSearchTerm}
          setSelectedCategory={setSelectedCategory}
          setSelectedVideo={handleVideoPlay}
          currentPage={currentPage}
          totalPages={totalPages}
          goToPage={goToPage}
          goToNextPage={goToNextPage}
          goToPreviousPage={goToPreviousPage}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
        />

        <EpisodesCTA />

        {/* Video Modal */}
        <VideoEmbed
          isOpen={!!selectedVideo}
          onClose={() => setSelectedVideo(null)}
          youtubeId={selectedVideo?.youtubeId}
          vimeoId={selectedVideo?.vimeoId}
          videoPlatform={selectedVideo?.videoPlatform}
        />
      </div>
      <Footer />
    </>
  );
};

export default Episodes;
